import { useState } from "react";
import { Building2, User, ShieldCheck, CornerDownRight, ArrowRight, ChevronDown, ChevronRight } from "lucide-react";

function countDesc(node) {
  let n = 0;
  const stack = [...(node.children || [])];
  while (stack.length) {
    const x = stack.pop();
    n++;
    for (const k of (x.children || [])) stack.push(k);
  }
  return n;
}

function flattenTree(children, collapsed) {
  const rows = [];
  const stack = (children || []).slice().reverse().map((n) => ({ node: n, depth: 0, parentName: null, path: [] }));
  while (stack.length) {
    const item = stack.pop();
    const hiddenByAncestor = item.path.some((id) => collapsed.has(id));
    const hasChildren = (item.node.children || []).length > 0;
    if (!hiddenByAncestor) rows.push({ ...item, hasChildren, descCount: hasChildren ? countDesc(item.node) : 0 });
    const kids = item.node.children || [];
    for (let i = kids.length - 1; i >= 0; i--) {
      stack.push({ node: kids[i], depth: item.depth + 1, parentName: item.node.name, path: [...item.path, item.node.company_id] });
    }
  }
  return rows;
}

function kr(v) {
  if (!v || v <= 0) return null;
  if (v >= 1e6) return `${(v / 1e6).toLocaleString("nb-NO", { maximumFractionDigits: 1 })} M kr`;
  if (v >= 1e3) return `${Math.round(v / 1e3)} k kr`;
  return `${Math.round(v)} kr`;
}

function Badge({ tone, children, testid }) {
  const tones = {
    blue: "bg-blue-500/10 text-blue-700 dark:text-blue-300",
    violet: "bg-violet-500/10 text-violet-700 dark:text-violet-300",
    neutral: "bg-muted text-muted-foreground",
    green: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  };
  return <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${tones[tone]}`}>{children}</span>;
}

export function OwnershipTree({ tree, onSelect }) {
  const [collapsed, setCollapsed] = useState(() => new Set());
  if (!tree) return null;
  const RootIcon = tree.root && tree.root.type === "person" ? User : Building2;
  const rows = flattenTree(tree.children, collapsed);

  const toggle = (id) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <div data-testid="ownership-tree">
      {/* Forklaring / legend */}
      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-blue-500" /> Direkte eierskap</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-violet-400" /> Eid via et selskap (indirekte)</span>
        <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> Kontroll (mer enn 50 %)</span>
      </div>

      {/* Rot: deg / valgt perspektiv */}
      <div className="flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 p-3">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary text-primary-foreground">
          <RootIcon className="h-4 w-4" strokeWidth={1.8} />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-primary">Utgangspunkt</p>
          <p className="truncate text-sm font-semibold">{tree.root && tree.root.name}</p>
        </div>
      </div>

      {rows.length > 0 ? (
        <div className="mt-2 space-y-2 overflow-x-auto">
          {rows.map(({ node, depth, parentName, hasChildren, descCount }) => {
            const direct = depth === 0;
            const isCollapsed = collapsed.has(node.company_id);
            const showEff = node.effective_pct != null && Math.abs((node.effective_pct || 0) - (node.edge_pct || 0)) > 0.01;
            const value = kr(((node.effective_pct || 0) / 100) * (node.valuation || 0));
            return (
              <div
                key={node.company_id + ":" + depth}
                style={{ marginLeft: `${depth * 24}px` }}
                className={`group relative flex items-center gap-2 rounded-xl border border-border/60 bg-card p-3 transition-colors ${direct ? "border-l-[3px] border-l-blue-500 bg-blue-500/[0.03]" : "border-l-[3px] border-l-violet-400/70"} ${onSelect ? "hover:bg-muted/50" : ""}`}
              >
                {depth > 0 ? (
                  <>
                    <span className="absolute -left-[15px] top-1/2 h-px w-3 bg-border" aria-hidden="true" />
                    <span className="absolute -left-[15px] bottom-1/2 top-0 w-px bg-border" aria-hidden="true" />
                  </>
                ) : null}

                {hasChildren ? (
                  <button
                    type="button"
                    data-testid={`tree-toggle-${node.company_id}`}
                    onClick={(e) => { e.stopPropagation(); toggle(node.company_id); }}
                    className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    aria-label={isCollapsed ? "Vis underliggende selskaper" : "Skjul underliggende selskaper"}
                  >
                    {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                ) : (
                  <span className="w-6 shrink-0" aria-hidden="true" />
                )}

                <button
                  type="button"
                  data-testid={`tree-node-${node.company_id}`}
                  onClick={onSelect ? () => onSelect(node.company_id) : undefined}
                  className={`flex min-w-0 flex-1 items-center gap-3 text-left ${onSelect ? "cursor-pointer" : "cursor-default"}`}
                >
                  <div className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${direct ? "bg-blue-500/10 text-blue-600 dark:text-blue-400" : "bg-violet-500/10 text-violet-600 dark:text-violet-400"}`}>
                    <Building2 className="h-4 w-4" strokeWidth={1.7} />
                  </div>
                  <div className="min-w-0 flex-1">
                    {direct ? (
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">Direkte eierskap</p>
                    ) : (
                      <p className="flex items-center gap-1 truncate text-[10px] font-semibold uppercase tracking-wider text-violet-600 dark:text-violet-400" data-testid={`tree-via-${node.company_id}`}>
                        <CornerDownRight className="h-3 w-3 shrink-0" /> Eid via {parentName}
                      </p>
                    )}
                    <p className="truncate text-sm font-medium group-hover:text-primary">{node.name}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      {direct ? (
                        <Badge tone="blue">Du eier {node.edge_pct}% direkte</Badge>
                      ) : (
                        <Badge tone="violet">{parentName} eier {node.edge_pct}%</Badge>
                      )}
                      {(!direct || showEff) ? <Badge tone="neutral">Din andel {node.effective_pct}%</Badge> : null}
                      {node.controlled ? (
                        <Badge tone="green" testid={`tree-control-${node.company_id}`}><ShieldCheck className="h-3 w-3" /> Kontroll</Badge>
                      ) : null}
                      {isCollapsed && descCount > 0 ? (
                        <Badge tone="neutral" testid={`tree-hidden-${node.company_id}`}>+{descCount} skjult</Badge>
                      ) : null}
                    </div>
                  </div>
                  {value ? (
                    <div className="shrink-0 pl-2 text-right" data-testid={`tree-value-${node.company_id}`}>
                      <p className="font-headings text-sm font-bold">{value}</p>
                      <p className="text-[10px] text-muted-foreground">din verdi</p>
                    </div>
                  ) : null}
                  {onSelect ? <ArrowRight className="h-4 w-4 shrink-0 -translate-x-1 text-muted-foreground opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" /> : null}
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">Ingen eierandeler i denne visningen.</p>
      )}
    </div>
  );
}
